import { CHANNEL_ID, TIMESTAMP } from '@@types/common';
import { AllLog } from '@@types/Log';
import LogFile, { DummyLogFile } from '@@types/LogFile';

import { ClipboardEvent } from '~/components/withFilePastDrop';
import { MAX_PREVIEW_ABLE_IMAGE_SIZE, URLS } from '~/constants';
import { capitalize, forEach, includes, isEmpty, isUndefined, memoize } from 'lodash';
import moment from 'moment';
import {
  __,
  allPass,
  anyPass,
  complement,
  concat,
  converge,
  curry,
  equals,
  gt,
  identity,
  ifElse,
  keys,
  length,
  lte,
  not,
  path,
  pipe,
  prop,
  propOr,
} from 'ramda';
import { Either } from 'ramda-fantasy';

import { eitherBy, includesInsensitive, rename } from './fnUtil/fnUtil';

const isBiggerDimension = (
  dimension1: { width: number; height: number },
  dimension2: { width: number; height: number }
): boolean => {
  return dimension1.width >= dimension2.width && dimension1.height >= dimension2.height;
};

export const getThumbnailJson = eitherBy(path(['thumbsJson', 'thumb360']), path(['thumbsJson', 'thumb170']));

const isOriginBiggerThanThumbnail = converge<
  [Pick<LogFile, 'thumbsJson'>],
  boolean,
  { width: number; height: number },
  { width: number; height: number }
>(isBiggerDimension, [path(['thumbsJson', 'original']), getThumbnailJson]);

// getMimeType :: file -> string
export const getMimeType = eitherBy(prop('mimeType'), propOr('', 'type'));

export const makeDownloadUrl = ({ channelId, id }: { id: string; channelId: CHANNEL_ID }, isSharedChannel = false) => {
  return isSharedChannel
    ? `${URLS.V4_API}/channels/${channelId}/files/${id}`
    : `${URLS.V2_API}/channel/${channelId}/file/${id}`;
};

const getThumbnailQueryParam = (file: Pick<LogFile, 'mimeType' | 'thumbsJson'>): string => {
  if (isGifImage(file)) {
    return '';
  }
  return !isEmpty(file.thumbsJson.thumb360) && isOriginBiggerThanThumbnail(file) ? '?isThumb=true' : '';
};

export const makeThumbnailUrl = (
  file: Pick<LogFile, 'channelId' | 'id' | 'thumbsJson' | 'mimeType'>,
  isSharedChannel: boolean
) =>
  converge<[Pick<LogFile, 'thumbsJson' | 'mimeType'>], string, string, string>(concat, [
    (file: Pick<LogFile, 'channelId' | 'id' | 'mimeType'>) => makeDownloadUrl(file, isSharedChannel),
    getThumbnailQueryParam,
  ])(file);

// getFileIconName :: mimeType -> fileIconName
export const getFileIconName = (mimeType: string) => {
  const prefix = 'file';
  const DEFAULT_NAME = 'etc';
  const fileTypeList = {
    ai: ['illustrator'],
    // apk: [],
    audio: ['audio'],
    eml: ['eml', 'message/rfc822'],
    excel: ['excel', 'spreadsheet', 'xlsx'],
    // gdoc: [],
    // googleEtc: [],
    // gsheet: [],
    // gslides: [],
    // hwp: [],
    image: ['image'],
    // keynote: [],
    // markup: [],
    // number: [],
    // pages: [],
    pdf: ['pdf'],
    presentation: ['powerpoint', 'presentation', 'pptx'],
    psd: ['photoshop'],
    // raw: [],
    txt: ['text/plain'],
    video: ['video'],
    word: ['msword', 'word', 'docx'],
    // xps: [],
    zip: ['compressed', 'compress', 'zip'],
  };

  let iconName = DEFAULT_NAME;

  forEach(fileTypeList, (fileType, className) => {
    forEach(fileType, (type) => {
      if (includes(mimeType, type)) {
        iconName = className;
        return false;
      }
      return true;
    });

    return iconName === DEFAULT_NAME;
  });

  return `${prefix}${capitalize(iconName)}`;
};

export const isExpired = (expireAt: TIMESTAMP) => {
  if (isUndefined(expireAt)) {
    return false;
  }
  return moment().isAfter(expireAt);
};

export const isNonExpired = complement(isExpired);

const getSize = eitherBy(prop('size'), prop('fileSize'));

export const isPreviewAbleSize = pipe<any, any, any>(getSize, lte(__, MAX_PREVIEW_ABLE_IMAGE_SIZE));

// isImageMime :: mimeType -> Bool
export const isImageMime = includesInsensitive('image/');

export const isGifImage = pipe<any, any, any>(getMimeType, allPass([isImageMime, includesInsensitive('gif')]));

// isNotPhotoShopMime :: mimeType -> Bool
const isNotPhotoShopMime = pipe(includesInsensitive('photoshop'), not);

// isImage :: file -> Bool
export const isImage = pipe(getMimeType, allPass([isImageMime, isNotPhotoShopMime]));

// isVideo :: file -> Bool
export const isVideo = pipe(getMimeType, includesInsensitive('video/'));

// hasThumbnail :: file -> bool
export const hasThumbnail = pipe(propOr({}, 'thumbsJson'), keys, length, gt(__, 1));

// isRotatedHorizontally :: file -> bool
const isRotatedHorizontally = pipe(prop('imageExifRotation'), anyPass([equals(6), equals(8)]));

// reverseDimension :: dimension -> dimension
const reverseDimension = rename({ width: 'height', height: 'width' });

const fixRotatedImageDimension = (
  file: LogFile,
  dimension: { width: number; height: number }
): { width: number; height: number } => {
  return isRotatedHorizontally(file) ? reverseDimension(dimension) : dimension;
};

// getThumbnailDimension :: file -> dimension
export const getThumbnailDimension = converge<
  [Pick<LogFile, 'thumbsJson'>],
  { width: number; height: number },
  Pick<LogFile, 'thumbsJson'>,
  { width: number; height: number }
>(fixRotatedImageDimension, [
  identity,
  ifElse<[Pick<LogFile, 'thumbsJson'>], { width: number; height: number }, { width: number; height: number }>(
    allPass([hasThumbnail, isOriginBiggerThanThumbnail]),
    getThumbnailJson,
    path(['thumbsJson', 'original'])
  ),
]);

export const correctThreadPanelDimension = ({ width, height }: { width: number; height: number }, maxWidth = 165) => {
  return {
    width: width > maxWidth ? maxWidth : width,
    height: height > 100 ? 100 : height,
  };
};

export const isForbiddenExtensionFile = propOr(false, 'forbiddenExtensionFlag');

export const isNonForbiddenExtensionFile = complement(isForbiddenExtensionFile);

export const validateFileForbidden = (file: { forbiddenExtensionFlag: boolean }): Either<string, LogFile> => {
  return (isForbiddenExtensionFile(file) ? Either.Left('forbidden file') : Either.Right(file)) as Either<
    string,
    LogFile
  >;
};

export const convertBlobToFile = (
  blob: Blob | File,
  name = `${moment().format('YYYY-MM-DD HH:mm:ss.SSS')}.png`
): File => {
  return new File([blob], name, { type: blob.type });
};

export const getRotationDegreeByExif = (exif: number) => {
  switch (exif) {
    case 5:
    case 6:
      return 90;
    case 3:
    case 4:
      return 180;
    case 7:
    case 8:
      return 270;
    case 1:
    case 2:
      return 0;
  }
};

export const isReversalByExif = (exif: number) => {
  const rotate = getRotationDegreeByExif(exif);
  return rotate === 90 || rotate === 270;
};

const supportFromImage = memoize(() => {
  return CSS.supports('image-orientation', 'from-image');
});

export const getExif = (exif: number) => {
  return supportFromImage() ? 1 : exif;
};

export const getThumbnailExif = (exif: number, thumbnailExif?: number) => {
  return thumbnailExif ?? (supportFromImage() && ![3, 6, 8].includes(exif) ? 1 : exif);
};

export const isEditableImageFile = pipe(
  getMimeType,
  anyPass([
    includesInsensitive('jpg'),
    includesInsensitive('jpeg'),
    includesInsensitive('png'),
    includesInsensitive('bmp'),
  ])
);

export const canPreviewImageFile = pipe(
  getMimeType,
  anyPass([
    includesInsensitive('jpg'),
    includesInsensitive('jpeg'),
    includesInsensitive('png'),
    includesInsensitive('bmp'),
    includesInsensitive('gif'),
  ])
);

export const nonPreviewImageFile = complement(canPreviewImageFile);

export const getImageSize = (file: File): Promise<{ width: number; height: number }> => {
  const URL = window.URL || window.webkitURL;
  const image = new Image();

  return new Promise((resolve, reject) => {
    image.onload = function onLoad(this: HTMLImageElement) {
      URL.revokeObjectURL(this.src);
      if (this.width) {
        resolve({ width: this.width, height: this.height });
      } else {
        reject(file);
      }
    };

    image.onerror = function onError() {
      URL.revokeObjectURL(this.src);
      reject(file);
    };

    image.src = URL.createObjectURL(file);
  });
};

export const canRenderImage = allPass([
  isNonForbiddenExtensionFile,
  isImage,
  (file) => isNonExpired(file.expireAt),
  isPreviewAbleSize,
  anyPass([isGifImage, hasThumbnail]),
]);

export const canRenderFileLog = (log: AllLog) => {
  return 'file' in log && log.file && canRenderImage(log.file);
};

export const canRenderFile = (logFile: LogFile | DummyLogFile) => {
  return canRenderImage(logFile);
};

export const nonRenderFileLog = complement(canRenderFileLog);

export const isImageType = pipe<any, any, any>(getMimeType, includesInsensitive('image/'));

export const isImageFromExcelEvent = (e: ClipboardEvent) => {
  const htmlData = e.clipboardData.getData('text/html');
  // 엑셀 붙여넣기 시 이미지 업로드 제거 예외처리
  return htmlData.indexOf('xmlns:x="urn:schemas-microsoft-com:office:excel"') > -1;
};

export const preload = curry((isSharedFile: boolean, images: LogFile[]) => {
  forEach(images, (img: LogFile) => {
    const image = new Image();
    image.src = makeDownloadUrl(img, isSharedFile);
  });
});
